import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import MarketingHeader from '../components/MarketingHeader';
import MarketingFooter from '../components/MarketingFooter';
import {
  Play, ArrowRight, Check, Mic, MessageCircle,
  Plus, FileText, Lock, X, User
} from 'lucide-react';
import InstallPWANudge from '../components/InstallPWANudge';
import { getCachedAuthUser } from '../utils/authCache';
import { shouldShowNudge } from '../utils/installPrompt';
import api from '../api';

/*
 * Landing page — pixel-faithful rebuild of the marketing artifact
 * (`cuminjar-landing.html`) delivered by the founder. Keeps the same
 * warm-paper palette, Fraunces serif hero, and section order.
 * The hero uses a static Paati mockup image (no live audio here — the
 * real audio player lives inside the app).
 */

const TESTIMONIALS = [
  { quote: 'My grandmother’s recipes were never written down. Now her voice reads them to my daughter. That’s everything.', name: 'Sameera R.', city: 'Bengaluru, India', mono: 'S', color: '#3D5A3A' },
  { quote: 'Hearing Appa tell the story in his own voice brings me right back home, all the way from California.', name: 'Arjun K.', city: 'San Jose, USA', mono: 'A', color: '#C7851C' },
  { quote: 'We live in different cities now, but our traditions stay alive — and our kids can actually hear them.', name: 'Priya S.', city: 'Toronto, Canada', mono: 'P', color: '#A83E2B' },
];

export default function Landing() {
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);
  const [showPwaNudge, setShowPwaNudge] = useState(false);

  useEffect(() => {
    if (!shouldShowNudge()) return;
    const t = setTimeout(() => setShowPwaNudge(true), 4000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!showVideo) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setShowVideo(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [showVideo]);

  // Auto-redirect returning users straight into /app. `?stay=1` opts out.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stay') === '1') return;
    if (getCachedAuthUser()) { navigate('/app', { replace: true }); return; }
    let cancelled = false;
    api.authMe()
      .then((u) => { if (u && !cancelled) navigate('/app', { replace: true }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <div className="bg-[#FBF6EC] text-[#282A1E]" style={{ fontFamily: "'Hanken Grotesk', system-ui, sans-serif" }}>
      <MarketingHeader />

      {/* HERO */}
      <section className="max-w-[1120px] mx-auto px-6 lg:px-7 pt-10 lg:pt-14 pb-10 lg:pb-12 grid lg:grid-cols-[1.02fr_.98fr] gap-8 lg:gap-12 items-center">
        <div>
          <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold tracking-[0.06em] text-[#A66C12] mb-4">
            <span className="w-[22px] h-[1.5px] bg-[#C7851C] opacity-60" />
            PRESERVE · SHARE · TREASURE
          </span>
          <h1
            className="font-semibold text-[34px] sm:text-[42px] lg:text-[50px] leading-[1.05] tracking-[-0.02em]"
            style={{ fontFamily: "'Fraunces', Georgia, serif" }}
          >
            The stories in their head aren’t{' '}
            <em className="italic font-medium text-[#3D5A3A]">written down anywhere.</em>
          </h1>
          <p className="mt-5 text-[16.5px] sm:text-[17.5px] text-[#6F6A58] max-w-[40ch] leading-[1.55]">
            A lifetime of memories, wisdom and recipes lives in your parents’ voices. CuminJar lets them just{' '}
            <b className="font-semibold text-[#282A1E]">talk</b> — in any language — then writes it in English and keeps their voice forever.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3.5">
            <Link
              to="/pricing?buy=1"
              data-testid="hero-primary-cta"
              className="inline-flex items-center gap-2 bg-[#3D5A3A] hover:bg-[#2E472C] text-white font-semibold text-[15.5px] px-6 py-3.5 rounded-[13px] shadow-[0_6px_16px_rgba(61,90,58,.25)] transition-all hover:-translate-y-0.5"
            >
              Start your family jar
              <ArrowRight size={17} />
            </Link>
            <button
              type="button"
              onClick={() => setShowVideo(true)}
              data-testid="watch-how-it-works-btn"
              className="inline-flex items-center gap-2 bg-white text-[#6F6A58] font-semibold text-[15px] px-[18px] py-[13px] rounded-[12px] border border-[#DFD7C3] hover:shadow-[0_1px_2px_rgba(48,54,28,.05),0_2px_8px_rgba(48,54,28,.05)] transition-shadow"
            >
              <Play size={16} className="text-[#3D5A3A]" fill="currentColor" />
              See how it works
            </button>
          </div>
          <div className="mt-8 flex items-center gap-3.5">
            <div className="flex">
              {[
                { c: '#3D5A3A', l: 'S' },
                { c: '#C7851C', l: 'A' },
                { c: '#A83E2B', l: 'P' },
                { c: '#6F6A58', l: 'R' },
              ].map((a, i) => (
                <span
                  key={a.l}
                  className="w-[34px] h-[34px] rounded-full border-[2.5px] border-[#FBF6EC] grid place-items-center font-bold text-[13px] text-white"
                  style={{ background: a.c, marginLeft: i === 0 ? 0 : -9 }}
                >
                  {a.l}
                </span>
              ))}
            </div>
            <p className="text-[13.5px] text-[#6F6A58] max-w-[24ch] leading-[1.4]">
              Families in Chennai, San Jose &amp; Toronto are filling their jars.
            </p>
          </div>
        </div>

        {/* Static hero mockup — no live audio on the marketing page.
            The real audio playback lives inside the app. */}
        <div className="relative">
          <img
            src="/paati-hero.png"
            alt="Paati's Sambar recipe playing on the CuminJar app, framed with star anise, cardamom pods and a handwritten recipe page"
            loading="eager"
            decoding="async"
            className="w-full max-w-[520px] mx-auto"
            data-testid="hero-mockup-image"
          />
        </div>
      </section>

      {/* TWO WAYS TO FILL YOUR JAR */}
      <section className="py-14 lg:py-16">
        <div className="max-w-[1120px] mx-auto px-6 lg:px-7">
          <div className="max-w-[60ch]">
            <div className="text-[12.5px] font-semibold tracking-[0.06em] text-[#A66C12] mb-3">
              TWO WAYS TO FILL YOUR JAR
            </div>
            <h2
              className="font-semibold text-[27px] sm:text-[33px] leading-[1.14] tracking-[-0.015em]"
              style={{ fontFamily: "'Fraunces', Georgia, serif" }}
            >
              Record your own memories — or{' '}
              <em className="italic font-medium text-[#3D5A3A]">invite the ones you love</em> to record theirs.
            </h2>
            <p className="mt-3 text-[15.5px] text-[#6F6A58]">
              Jot down your own recipes and stories, or hear your parents and grandparents tell them. CuminJar does both — in minutes, in their voice.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mt-9">
            {/* For yourself */}
            <article className="bg-white border border-[#EAE3D3] rounded-[20px] p-7 flex flex-col">
              <span className="inline-flex items-center gap-2 self-start text-[12.5px] font-semibold px-3 py-[5px] rounded-full mb-4 bg-[#E5EDE2] text-[#3D5A3A]">
                <User size={14} /> For yourself
              </span>
              <h3 className="font-semibold text-[21px] leading-[1.2] tracking-[-0.01em]" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                Record your own recipes &amp; memories
              </h3>
              <p className="mt-2 text-[14.5px] text-[#6F6A58]">
                Sit down with a cup of chai, tap record, and just talk. Save the dishes you cook and the stories only you know.
              </p>
              <ul className="mt-4 mb-5 space-y-[9px] text-[14px]">
                {[
                  'Talk in any language — we translate',
                  'We write it into a clean, readable page',
                  'Your voice preserved on every page',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <Check size={17} className="text-[#3D5A3A] flex-none mt-[1px]" strokeWidth={2.6} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/pricing?buy=1')}
                data-testid="two-ways-record-cta"
                className="mt-auto self-start inline-flex items-center gap-[7px] font-semibold text-[14.5px] text-[#3D5A3A] group"
              >
                Start recording
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-[3px]" />
              </button>
            </article>

            {/* With loved ones */}
            <article className="bg-white border border-[#EAE3D3] rounded-[20px] p-7 flex flex-col">
              <span className="inline-flex items-center gap-2 self-start text-[12.5px] font-semibold px-3 py-[5px] rounded-full mb-4 bg-[#F5E7C6] text-[#A66C12]">
                <MessageCircle size={14} /> With loved ones
              </span>
              <h3 className="font-semibold text-[21px] leading-[1.2] tracking-[-0.01em]" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                Invite parents &amp; grandparents on WhatsApp
              </h3>
              <p className="mt-2 text-[14.5px] text-[#6F6A58]">
                Pick a warm prompt like “Amma, tell us how you make rasam,” send it in a tap. They open the link, press mic, and speak.
              </p>
              <ul className="mt-4 mb-5 space-y-[9px] text-[14px]">
                {[
                  'No login, no app for them to install',
                  'Warm prompts included — shuffle for ideas',
                  'Every recording saved in their own voice',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5">
                    <Check size={17} className="text-[#3D5A3A] flex-none mt-[1px]" strokeWidth={2.6} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/pricing?buy=1')}
                data-testid="two-ways-invite-cta"
                className="mt-auto self-start inline-flex items-center gap-[7px] font-semibold text-[14.5px] text-[#3D5A3A] group"
              >
                Invite family
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-[3px]" />
              </button>
            </article>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-14 lg:py-16 bg-[#F5EEDF] border-y border-[#EAE3D3]" style={{ scrollMarginTop: '80px' }}>
        <div className="max-w-[1120px] mx-auto px-6 lg:px-7">
          <div className="text-center max-w-[60ch] mx-auto">
            <div className="text-[12.5px] font-semibold tracking-[0.06em] text-[#A66C12] mb-3">HOW IT WORKS</div>
            <h2 className="font-semibold text-[27px] sm:text-[33px] leading-[1.14] tracking-[-0.015em]" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              Simple. Personal. Timeless.
            </h2>
            <p className="mt-3 text-[16px] text-[#6F6A58]">
              No typing, no forms, no decisions. Just pass them the phone and let them talk.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mt-9">
            {[
              { icon: Plus, tint: 'green', n: 'Step one', title: 'Create your family jar', desc: 'Sign up in 30 seconds — no forms, no fuss. Your jar is ready instantly.' },
              { icon: Mic, tint: 'turmeric', n: 'Step two', title: 'Talk — or ask family to', desc: 'Record your own memories, or send a WhatsApp prompt and let Amma or Nani speak. Any language works.' },
              { icon: FileText, tint: 'rust', n: 'Step three', title: 'We save it, forever', desc: 'CuminJar transcribes, translates and organises every entry — then binds it into a hardbound book you can hold.' },
            ].map((s) => {
              const Icon = s.icon;
              const tintStyles = {
                green: { bg: '#E5EDE2', color: '#3D5A3A' },
                turmeric: { bg: '#F5E7C6', color: '#A66C12' },
                rust: { bg: '#F2DCD3', color: '#A83E2B' },
              }[s.tint];
              return (
                <div key={s.n} className="bg-white border border-[#EAE3D3] rounded-[18px] p-[26px_24px]">
                  <div
                    className="w-[46px] h-[46px] rounded-[13px] grid place-items-center mb-[15px]"
                    style={{ background: tintStyles.bg, color: tintStyles.color }}
                  >
                    <Icon size={23} strokeWidth={1.85} />
                  </div>
                  <div className="text-[14px] font-semibold text-[#A39C88] mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                    {s.n}
                  </div>
                  <h3 className="text-[17px] font-semibold tracking-[-0.01em]">{s.title}</h3>
                  <p className="text-[14px] text-[#6F6A58] mt-[7px] leading-[1.5]">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRANSLATION MAGIC */}
      <section className="py-14 lg:py-16">
        <div className="max-w-[1120px] mx-auto px-6 lg:px-7">
          <div className="text-center max-w-[60ch] mx-auto">
            <div className="text-[12.5px] font-semibold tracking-[0.06em] text-[#A66C12] mb-3">
              THE MAGIC IN THE MIDDLE
            </div>
            <h2 className="font-semibold text-[27px] sm:text-[33px] leading-[1.14] tracking-[-0.015em]" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              She speaks her language. Your kids read <em className="italic font-medium text-[#3D5A3A]">theirs.</em>
            </h2>
            <p className="mt-3 text-[16px] text-[#6F6A58]">
              The reason CuminJar works for families spread across the world: the voice stays hers, the words become readable, and nothing is lost in between.
            </p>
          </div>

          <div className="grid md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3.5 items-stretch mt-10">
            <FlowCard
              icon={Mic}
              tintBg="#F5E7C6"
              tintFg="#A66C12"
              title="She talks"
              desc="In Tamil, Hindi, Telugu — whatever she’s most herself in."
              mini="“Naan solren, kelu…”"
            />
            <FlowArrow />
            <FlowCard
              icon={FileText}
              tintBg="#E5EDE2"
              tintFg="#3D5A3A"
              title="We write it"
              desc="Transcribed and translated into a clean English page."
              mini="“Let me tell you, listen…”"
            />
            <FlowArrow />
            <FlowCard
              icon={Play}
              tintBg="#F2DCD3"
              tintFg="#A83E2B"
              title="They hear her"
              desc="A QR on every page plays her real voice, untouched."
              mini="English for the young, voice for the soul."
            />
          </div>
        </div>
      </section>

      {/* BOOK */}
      <section id="legacy-book" className="py-16 lg:py-20 bg-[#F5EEDF] border-y border-[#EAE3D3]" style={{ scrollMarginTop: '80px' }}>
        <div className="max-w-[1120px] mx-auto px-6 lg:px-7 grid lg:grid-cols-[.95fr_1.05fr] gap-10 lg:gap-14 items-center">
        <div className="relative">
          <img
            src="/family-book-open.jpg"
            alt="An open hardbound Family Book showing a story about arriving in Madras, with a vintage grandmother photograph on the left page and a QR code on the right that plays the recording"
            loading="lazy"
            decoding="async"
            className="w-full max-w-[260px] mx-auto rounded-[14px] shadow-[0_20px_50px_rgba(48,54,28,.20)] border border-[#EAE3D3]"
            data-testid="family-book-photo"
          />
          {/* Small QR chip that lifts a real detail out of the photograph */}
          <div className="hidden md:flex absolute -bottom-5 -left-5 bg-white rounded-2xl border border-[#EAE3D3] shadow-[0_10px_30px_rgba(48,54,28,.12)] px-4 py-3 items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[#F5EEDF] grid place-items-center">
              <div className="w-7 h-7 grid grid-cols-6 grid-rows-6 gap-[1px]">
                {Array.from({ length: 36 }).map((_, i) => {
                  const on = (Math.sin(i * 2.31) + Math.cos(i * 1.7)) > 0;
                  return <div key={i} className={on ? 'bg-[#2C2418]' : 'bg-transparent'} />;
                })}
              </div>
            </div>
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.14em] text-[#A39C88] font-semibold">On every page</p>
              <p className="text-[13px] font-semibold text-[#282A1E]">Scan to hear Grandma’s voice</p>
            </div>
          </div>
        </div>
          <div>
            <div className="text-[12.5px] font-semibold tracking-[0.06em] text-[#A66C12] mb-3">
              THE FAMILY BOOK
            </div>
            <h2 className="font-semibold text-[27px] sm:text-[33px] leading-[1.15] tracking-[-0.015em]" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              A hardbound heirloom for the next generation.
            </h2>
            <p className="mt-4 text-[15.5px] text-[#6F6A58] max-w-[46ch] leading-[1.55]">
              When your jar feels full, we turn it into a beautiful printed book — every story, recipe and memory typeset on warm cream paper with an{' '}
              <b className="text-[#282A1E]">olive-green fabric binding</b>. Beside the English on every page sits a small QR code: point a phone at it and hear the original voice.
            </p>
            <ul className="mt-5 space-y-[11px] text-[14.5px]">
              {[
                'Printed & bound in India, delivered worldwide',
                'QR on every page plays the original recording',
                'Stories & recipes, organised by generation',
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Check size={18} className="text-[#3D5A3A] flex-none mt-[1px]" strokeWidth={2.6} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <span className="mt-6 inline-flex items-center gap-2 bg-[#E5EDE2] text-[#2E472C] text-[13.5px] font-semibold px-4 py-[9px] rounded-[11px]">
              <Lock size={16} />
              Order your book once your jar holds 30 memories
            </span>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="stories" className="py-14 lg:py-16">
        <div className="max-w-[1120px] mx-auto px-6 lg:px-7">
          <div className="text-center max-w-[60ch] mx-auto">
            <div className="text-[12.5px] font-semibold tracking-[0.06em] text-[#A66C12] mb-3">
              LOVED BY FAMILIES
            </div>
            <h2 className="font-semibold text-[27px] sm:text-[33px] leading-[1.14] tracking-[-0.015em]" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              Voices worth keeping.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-9">
            {TESTIMONIALS.map((t) => (
              <article key={t.name} className="bg-white border border-[#EAE3D3] rounded-[18px] p-6">
                <p className="text-[16px] leading-[1.5] text-[#282A1E]" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                  “{t.quote}”
                </p>
                <div className="flex items-center gap-3 mt-[18px]">
                  <span className="w-10 h-10 rounded-full grid place-items-center font-bold text-[15px] text-white flex-none" style={{ background: t.color }}>
                    {t.mono}
                  </span>
                  <div>
                    <div className="font-semibold text-[14.5px]">{t.name}</div>
                    <div className="text-[12.5px] text-[#6F6A58]">{t.city}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="pricing-cta" className="py-14 lg:py-16">
        <div className="max-w-[1120px] mx-auto px-6 lg:px-7">
          <div className="relative overflow-hidden bg-[#3D5A3A] text-[#F7F2E6] rounded-[26px] px-8 sm:px-10 lg:px-14 py-12 lg:py-[52px] text-center">
            <div
              className="absolute -right-16 -top-16 w-[240px] h-[240px] pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(199,133,28,.25), transparent 70%)' }}
              aria-hidden="true"
            />
            <div
              className="absolute -left-12 -bottom-[70px] w-[220px] h-[220px] pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(168,62,43,.22), transparent 70%)' }}
              aria-hidden="true"
            />
            <h2 className="relative font-semibold text-[26px] sm:text-[34px] leading-[1.15] tracking-[-0.015em]" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              Start your family jar today.
            </h2>
            <p className="relative text-[16px] text-[rgba(247,242,230,.82)] mt-3.5 max-w-[46ch] mx-auto">
              Record their voice. Save their stories. Pass down everything your family knows — before it’s gone.
            </p>
            <div className="relative mt-[22px] text-[15px] text-[#F5E7C6]" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
              <b className="text-[30px] font-semibold">₹3,999</b>&nbsp;&nbsp;·&nbsp;&nbsp;one-time, not a subscription
            </div>
            <Link
              to="/pricing?buy=1"
              data-testid="final-cta"
              className="relative mt-[22px] inline-flex items-center gap-2 bg-[#F5E7C6] hover:bg-white text-[#2E472C] font-semibold text-[15.5px] px-6 py-3.5 rounded-[13px] shadow-[0_8px_20px_rgba(0,0,0,.2)] transition-all hover:-translate-y-0.5"
            >
              Start your family jar
              <ArrowRight size={17} />
            </Link>
            <div className="relative mt-[15px] text-[12.5px] text-[rgba(247,242,230,.6)]">
              Unlimited recordings · invite the whole family · hardbound book included
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
      <InstallPWANudge open={showPwaNudge} onClose={() => setShowPwaNudge(false)} />

      {showVideo && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4 py-6 animate-in fade-in duration-200"
          onClick={() => setShowVideo(false)}
          data-testid="explainer-video-modal"
        >
          <button
            type="button"
            aria-label="Close video"
            onClick={() => setShowVideo(false)}
            data-testid="close-explainer-video"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <X size={22} />
          </button>
          <div
            className="w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              key="cj-explainer"
              poster="/cuminjar-explainer-poster.jpg"
              controls
              autoPlay
              playsInline
              preload="metadata"
              className="w-full h-full object-contain bg-black"
              data-testid="explainer-video-player"
            >
              <source src="/cuminjar-explainer.mp4" type="video/mp4" />
              Your browser doesn’t support this video.
            </video>
          </div>
          <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70 text-[12px]">Tap anywhere outside the video to close</p>
        </div>
      )}
    </div>
  );
}

/* ---------- Translation flow helpers ---------- */
function FlowCard({ icon: Icon, tintBg, tintFg, title, desc, mini }) {
  return (
    <div className="bg-white border border-[#EAE3D3] rounded-[16px] px-5 py-[22px] text-center">
      <div
        className="w-[44px] h-[44px] rounded-[12px] mx-auto mb-3.5 grid place-items-center"
        style={{ background: tintBg, color: tintFg }}
      >
        <Icon size={22} strokeWidth={1.9} />
      </div>
      <h4 className="text-[15.5px] font-semibold">{title}</h4>
      <p className="text-[13px] text-[#6F6A58] mt-1.5 leading-[1.45]">{desc}</p>
      <div
        className="italic text-[13.5px] text-[#282A1E] mt-2.5 bg-[#FBF6EC] border border-[#EAE3D3] rounded-[9px] px-2.5 py-[7px]"
        style={{ fontFamily: "'Fraunces', Georgia, serif" }}
      >
        {mini}
      </div>
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="grid place-items-center text-[#A39C88] max-md:rotate-90 max-md:py-1" aria-hidden="true">
      <ArrowRight size={22} />
    </div>
  );
}

